import { useEffect, useState } from 'react';
// ...existing code...
import { BarChart3, Calendar, Clock, DollarSign, Download, Package, TrendingUp, Users } from 'lucide-react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input } from '../components/common/Input';
import Footer from '../components/layout/Footer';
import Sidebar from '../components/layout/Sidebar';
import ordersService from '../services/ordersService';
import { theme } from '../styles/theme';

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background};
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  padding: ${theme.spacing.xl};
  padding-bottom: 80px;

  @media (max-width: ${theme.breakpoints.lg}) {
    margin-left: 0;
    padding: ${theme.spacing.md};
    padding-top: 70px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm};
    padding-top: 70px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  
  h1 {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.text};
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid ${theme.colors.border};
  flex-wrap: wrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Tab = styled.button`
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border: none;
  background: transparent;
  border-bottom: 3px solid ${({ active }) => active ? theme.colors.primary : 'transparent'};
  color: ${({ active }) => active ? theme.colors.primary : theme.colors.textSecondary};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: ${theme.typography.fontSize.base};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  white-space: nowrap;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.sm};
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.md};
  flex-wrap: wrap;
  align-items: center;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const ChartCard = styled(Card)`
  padding: ${theme.spacing.xl};
  
  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.text};
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const SummaryCard = styled(Card)`
  padding: ${theme.spacing.xl};
  text-align: center;
  
  h3 {
    font-size: ${theme.typography.fontSize.sm};
    font-weight: ${theme.typography.fontWeight.regular};
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing.sm};
  }
  
  p {
    font-size: ${theme.typography.fontSize['2xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${theme.colors.primary};
  }
`;

const TableContainer = styled(Card)`
  padding: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${theme.breakpoints.sm}) {
    border-radius: ${theme.borderRadius.md};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 500px;

  @media (max-width: ${theme.breakpoints.sm}) {
    min-width: 400px;
  }
`;

const TableHeader = styled.thead`
  background-color: ${theme.colors.secondary};
`;

const TableHeaderRow = styled.tr`
  border-bottom: 2px solid ${theme.colors.border};
`;

const TableHeaderCell = styled.th`
  padding: ${theme.spacing.lg};
  text-align: left;
  font-weight: ${theme.typography.fontWeight.semiBold};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.xs};
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.secondary};
  }
`;

const TableCell = styled.td`
  padding: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text};
  white-space: nowrap;

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
    font-size: ${theme.typography.fontSize.sm};
  }
`;


function formatDailySales(dailySales) {
  // Convert { '2026-02-01': 1000, ... } to [ { name: 'Mon', sales: 1000 }, ... ] for current week
  if (!dailySales) return [];
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return Object.entries(dailySales).map(([date, sales]) => {
    const d = new Date(date);
    return { name: days[d.getDay()], sales };
  });
}

function formatCategorySales(categorySales) {
  if (!categorySales) return [];
  return Object.entries(categorySales).map(([name, value]) => ({ name, value }));
}

function formatMonthly(monthly) {
  if (!monthly) return [];
  return Object.entries(monthly).map(([month, v]) => ({ month, ...v }));
}

const COLORS = [theme.colors.primary, theme.colors.accent, theme.colors.accentTeal, theme.colors.status.preparing];

function Reports() {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    ordersService.getAnalytics({ start: dateRange.start, end: dateRange.end })
      .then(setAnalytics)
      .catch(e => setError(e.message || 'Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [dateRange]);

  const handleExport = (format) => {
    alert(`Exporting report as ${format}...`);
  };


  // TODO: Replace with real data from analytics when available
  const salesByItem = [];
  const salesByCategory = [
    { name: 'Starters', revenue: 45000, percentage: 18 },
    { name: 'Main Course', revenue: 150000, percentage: 60 },
    { name: 'Desserts', revenue: 30000, percentage: 12 },
    { name: 'Beverages', revenue: 25000, percentage: 10 },
  ];

  const peakHours = [
    { hour: '12:00 PM', orders: 45, revenue: 12500 },
    { hour: '1:00 PM', orders: 52, revenue: 14500 },
    { hour: '2:00 PM', orders: 38, revenue: 10500 },
    { hour: '7:00 PM', orders: 48, revenue: 13500 },
    { hour: '8:00 PM', orders: 55, revenue: 15500 },
    { hour: '9:00 PM', orders: 42, revenue: 12000 },
  ];

  const customerAnalytics = [
    { segment: 'New Customers', count: 45, percentage: 30 },
    { segment: 'Regular Customers', count: 75, percentage: 50 },
    { segment: 'VIP Customers', count: 30, percentage: 20 },
  ];

  const staffPerformance = [
    { name: 'John Doe', orders: 45, sales: 12500, rating: 4.8 },
    { name: 'Jane Smith', orders: 32, sales: 8900, rating: 4.9 },
    { name: 'Mike Johnson', orders: 28, sales: 7800, rating: 4.7 },
  ];

  const inventoryTurnover = [
    { item: 'Rice', usage: 450, stock: 50, turnover: 9 },
    { item: 'Flour', usage: 300, stock: 70, turnover: 4.3 },
    { item: 'Oil', usage: 250, stock: 75, turnover: 3.3 },
  ];

  const profitLoss = {
    revenue: 250000,
    cogs: 100000,
    expenses: 50000,
    grossProfit: 150000,
    netProfit: 100000,
  };

  return (
    <Container>
      <Sidebar />
      <MainContent>
        <Header>
          <h1>Reports & Analytics</h1>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <Button variant="outline" size="sm" onClick={() => handleExport('PDF')}>
              <Download size={18} />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('Excel')}>
              <Download size={18} />
              Excel
            </Button>
          </div>
        </Header>

        <FiltersBar>
          <Calendar size={20} style={{ color: theme.colors.textSecondary }} />
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            style={{ width: '200px' }}
          />
          <span style={{ color: theme.colors.textSecondary }}>to</span>
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            style={{ width: '200px' }}
          />
        </FiltersBar>

        <Tabs>
          <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            <BarChart3 size={18} />
            Overview
          </Tab>
          <Tab active={activeTab === 'sales'} onClick={() => setActiveTab('sales')}>
            <TrendingUp size={18} />
            Sales Analysis
          </Tab>
          <Tab active={activeTab === 'peak'} onClick={() => setActiveTab('peak')}>
            <Clock size={18} />
            Peak Hours
          </Tab>
          <Tab active={activeTab === 'customers'} onClick={() => setActiveTab('customers')}>
            <Users size={18} />
            Customer Analytics
          </Tab>
          <Tab active={activeTab === 'staff'} onClick={() => setActiveTab('staff')}>
            <Users size={18} />
            Staff Performance
          </Tab>
          <Tab active={activeTab === 'inventory'} onClick={() => setActiveTab('inventory')}>
            <Package size={18} />
            Inventory Turnover
          </Tab>
          <Tab active={activeTab === 'pl'} onClick={() => setActiveTab('pl')}>
            <DollarSign size={18} />
            Profit & Loss
          </Tab>
        </Tabs>

        {activeTab === 'overview' && (
          loading ? <div>Loading...</div> : error ? <div style={{color:'red'}}>{error}</div> : analytics && <>
            <SummaryCards>
              <SummaryCard>
                <h3>Total Revenue</h3>
                <p>₹{analytics.totalRevenue?.toLocaleString() ?? 0}</p>
              </SummaryCard>
              <SummaryCard>
                <h3>Total Orders</h3>
                <p>{analytics.totalOrders?.toLocaleString() ?? 0}</p>
              </SummaryCard>
              <SummaryCard>
                <h3>Average Order Value</h3>
                <p>₹{analytics.avgOrderValue?.toLocaleString() ?? 0}</p>
              </SummaryCard>
              <SummaryCard>
                <h3>Growth Rate</h3>
                <p>{analytics.growthRate ? `${analytics.growthRate}%` : '-'}</p>
              </SummaryCard>
            </SummaryCards>
            <ChartsGrid>
              <ChartCard>
                <h2>Daily Sales Trend</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={formatDailySales(analytics.dailySales)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="sales"
                      stroke={theme.colors.primary}
                      strokeWidth={2}
                      name="Sales (₹)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard>
                <h2>Category Sales Distribution</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={formatCategorySales(analytics.categorySales)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {formatCategorySales(analytics.categorySales).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard>
                <h2>Monthly Revenue & Orders</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={formatMonthly(analytics.monthly)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="revenue" fill={theme.colors.primary} name="Revenue (₹)" />
                    <Bar yAxisId="right" dataKey="orders" fill={theme.colors.accent} name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartsGrid>
          </>
        )}

        {activeTab === 'sales' && (
          <>
            <ChartCard>
              <h2>Sales by Item</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salesByItem}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="revenue" fill={theme.colors.primary} name="Revenue (₹)" />
                  <Bar dataKey="quantity" fill={theme.colors.accent} name="Quantity" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard>
              <h2>Sales by Category</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={salesByCategory}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name} ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="revenue"
                  >
                    {salesByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </>
        )}

        {activeTab === 'peak' && (
          <ChartCard>
            <h2>Peak Hours Analysis</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={peakHours}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="orders" fill={theme.colors.primary} name="Orders" />
                <Bar dataKey="revenue" fill={theme.colors.accent} name="Revenue (₹)" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {activeTab === 'customers' && (
          <>
            <ChartCard>
              <h2>Customer Segmentation</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={customerAnalytics}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ segment, percentage }) => `${segment} ${percentage}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {customerAnalytics.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <Card style={{ marginTop: theme.spacing.lg }}>
              <h3 style={{ marginBottom: theme.spacing.md }}>Customer Statistics</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: theme.spacing.lg }}>
                {customerAnalytics.map((segment) => (
                  <div key={segment.segment}>
                    <h4>{segment.segment}</h4>
                    <p style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary }}>
                      {segment.count} ({segment.percentage}%)
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}

        {activeTab === 'staff' && (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Staff Name</TableHeaderCell>
                  <TableHeaderCell>Orders Served</TableHeaderCell>
                  <TableHeaderCell>Total Sales</TableHeaderCell>
                  <TableHeaderCell>Avg Rating</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {staffPerformance.map((perf) => (
                  <TableRow key={perf.name}>
                    <TableCell><strong>{perf.name}</strong></TableCell>
                    <TableCell>{perf.orders}</TableCell>
                    <TableCell>₹{perf.sales.toLocaleString()}</TableCell>
                    <TableCell>{perf.rating} ⭐</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'inventory' && (
          <TableContainer>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Item</TableHeaderCell>
                  <TableHeaderCell>Usage</TableHeaderCell>
                  <TableHeaderCell>Current Stock</TableHeaderCell>
                  <TableHeaderCell>Turnover Ratio</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {inventoryTurnover.map((item) => (
                  <TableRow key={item.item}>
                    <TableCell><strong>{item.item}</strong></TableCell>
                    <TableCell>{item.usage}</TableCell>
                    <TableCell>{item.stock}</TableCell>
                    <TableCell>{item.turnover}x</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {activeTab === 'pl' && (
          <Card>
            <h2 style={{ marginBottom: theme.spacing.lg }}>Profit & Loss Statement</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: theme.spacing.lg }}>
              <div>
                <h3>Revenue</h3>
                <p style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary }}>
                  ₹{profitLoss.revenue.toLocaleString()}
                </p>
              </div>
              <div>
                <h3>Cost of Goods Sold</h3>
                <p style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.status.outOfStock }}>
                  ₹{profitLoss.cogs.toLocaleString()}
                </p>
              </div>
              <div>
                <h3>Operating Expenses</h3>
                <p style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.status.outOfStock }}>
                  ₹{profitLoss.expenses.toLocaleString()}
                </p>
              </div>
              <div>
                <h3>Gross Profit</h3>
                <p style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.status.ready }}>
                  ₹{profitLoss.grossProfit.toLocaleString()}
                </p>
              </div>
              <div>
                <h3>Net Profit</h3>
                <p style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.status.ready }}>
                  ₹{profitLoss.netProfit.toLocaleString()}
                </p>
              </div>
              <div>
                <h3>Profit Margin</h3>
                <p style={{ fontSize: theme.typography.fontSize['2xl'], fontWeight: theme.typography.fontWeight.bold, color: theme.colors.status.ready }}>
                  {((profitLoss.netProfit / profitLoss.revenue) * 100).toFixed(2)}%
                </p>
              </div>
            </div>
          </Card>
        )}
      </MainContent>
      <Footer />
    </Container>
  );
}

export default Reports;

